// frontend/packages/business-console/src/components/inbox/FormSnapshotCard.jsx

import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const HIDDEN_KEYS = new Set([
  "name",
  "fullName",
  "first_name",
  "firstName",
  "email",
  "Email",
  "phone",
  "Phone",
  "subject",
  "message",
]);

const formatValue = (value) => {
  if (value == null) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => `${k}: ${formatValue(v)}`)
      .join(", ");
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

/**
 * Header card at the top of a conversation thread. Shows the originating
 * form's "extra" fields (anything beyond name/email/phone/message — those
 * are already represented in the message bubbles and conversation header).
 * Collapsible so it doesn't dominate the thread on a phone.
 */
export default function FormSnapshotCard({ submission, definition }) {
  const [open, setOpen] = useState(false);

  const payload = submission?.payload || {};
  const fields = (definition?.fields || []).filter(
    (f) =>
      f &&
      f.key &&
      f.type !== "hidden" &&
      f.type !== "file" &&
      !HIDDEN_KEYS.has(f.key),
  );

  // Build label/value pairs using form definition labels where available,
  // falling back to raw payload keys for fields the definition doesn't cover.
  const rows = [];
  for (const field of fields) {
    const value = payload[field.key];
    if (value === undefined || value === null || value === "") continue;
    rows.push({
      label: field.label || field.key,
      value: formatValue(value),
    });
  }
  if (!fields.length) {
    for (const [key, value] of Object.entries(payload)) {
      if (HIDDEN_KEYS.has(key)) continue;
      const formatted = formatValue(value);
      if (!formatted) continue;
      rows.push({ label: key, value: formatted });
    }
  }

  if (!submission && !rows.length) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: "background.default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Form submission
          </Typography>
          {definition?.name && (
            <Chip
              size="small"
              label={definition.name}
              sx={{ height: 22, textTransform: "none" }}
            />
          )}
          {submission?.formVersion ? (
            <Chip
              size="small"
              variant="outlined"
              label={`v${submission.formVersion}`}
              sx={{ height: 22 }}
            />
          ) : null}
        </Box>

        {rows.length > 0 && (
          <Button
            size="small"
            onClick={() => setOpen((v) => !v)}
            endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: "none" }}
          >
            {open ? "Hide details" : `${rows.length} field${rows.length === 1 ? "" : "s"}`}
          </Button>
        )}
      </Box>

      <Collapse in={open} timeout="auto">
        {rows.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "160px 1fr" },
                rowGap: 1,
                columnGap: 2,
              }}
            >
              {rows.map((row) => (
                <React.Fragment key={row.label}>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    {row.label}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {row.value}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          </>
        )}
      </Collapse>
    </Paper>
  );
}
